import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { Modal } from "../components/Modal";
import { Pagination } from "../components/Pagination";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Mono,
  PageHeader,
  Select,
  Table,
  TableSkeleton,
  Td,
  Th,
  Tag,
  buttonStyles,
  cx,
} from "../components/ui";
import { ApiError } from "../lib/api";
import { HOURS_PER_MONTH, hourlyMicros, money, rate } from "../lib/money";
import { formatMemory } from "../lib/plans";
import { useCreatePlan, useDeletePlan, usePlans, useUpdatePlan } from "../lib/queries";
import type { Plan, PlanInput, PlanKind, PlanState } from "../lib/types";

/** Blank form state. Deliberately not a free tier — the catalogue has none. */
const EMPTY: PlanInput = {
  name: "",
  slug: "",
  description: "",
  cpuLimit: 1,
  memoryMb: 512,
  replicas: 1,
  storageGb: null,
  priceCents: 500,
  currency: "USD",
  state: "active",
  kind: "app",
  sortOrder: 0,
};

const LIMIT = 25;

const CURRENCIES = ["USD", "EUR", "GBP", "AUD", "CAD", "SGD", "IDR"];

/** Matches planSlugSchema on the API, so a bad slug fails here rather than there. */
const SLUG_PATTERN = "[a-z0-9-]+";

function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

/** Cents to the major-unit string the price field shows. */
function toMajor(cents: number): string {
  return (cents / 100).toFixed(2);
}

export function PlansPage() {
  const [offset, setOffset] = useState(0);
  const plans = usePlans({ limit: LIMIT, offset });
  const createPlan = useCreatePlan();
  const updatePlan = useUpdatePlan();
  const deletePlan = useDeletePlan();

  const [editing, setEditing] = useState<Plan | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<PlanInput>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Plan | null>(null);

  /*
   * The price field keeps its own string. Deriving its value from `priceCents`
   * on every keystroke would rewrite "12." to "12" mid-type and throw the caret
   * to the end — the usual controlled-number-input problem, and worse here
   * because the number sets live bills.
   */
  const [priceInput, setPriceInput] = useState(toMajor(EMPTY.priceCents));

  /*
   * Whether the operator has taken the slug over. Until they do it follows the
   * name, which is what they would have typed anyway — and typing it by hand is
   * where the lowercase-and-hyphens rule gets broken.
   */
  const [slugTouched, setSlugTouched] = useState(false);

  function openCreate() {
    setForm(EMPTY);
    setPriceInput(toMajor(EMPTY.priceCents));
    setSlugTouched(false);
    setEditing(null);
    setCreating(true);
    setError(null);
  }

  function openEdit(plan: Plan) {
    setForm({
      name: plan.name,
      slug: plan.slug,
      kind: plan.kind,
      description: plan.description ?? "",
      cpuLimit: plan.resources.cpu,
      memoryMb: plan.resources.memoryMb,
      /*
       * Always 1 since the API's migration 0151 moved the count onto the app.
       * Sent back as the literal rather than as whatever the row carries, so
       * a tier stored before that cannot be saved back with a figure the API
       * now refuses -- and the form no longer offers the field at all.
       */
      replicas: 1,
      storageGb: plan.storageGb,
      priceCents: plan.price.cents,
      currency: plan.price.currency,
      state: plan.state,
      sortOrder: plan.sortOrder,
    });
    setPriceInput(toMajor(plan.price.cents));
    // An existing slug is a URL and a customer-visible identifier; it follows
    // nothing automatically.
    setSlugTouched(true);
    setEditing(plan);
    setCreating(false);
    setError(null);
  }

  function onNameChange(name: string) {
    setForm((current) => ({
      ...current,
      name,
      slug: slugTouched ? current.slug : slugify(name),
    }));
  }

  function onPriceChange(raw: string) {
    setPriceInput(raw);
    const parsed = Number(raw);
    if (raw.trim() !== "" && Number.isFinite(parsed) && parsed >= 0) {
      setForm((current) => ({ ...current, priceCents: Math.round(parsed * 100) }));
    }
  }

  function close() {
    setEditing(null);
    setCreating(false);
    setError(null);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    const payload: PlanInput = {
      ...form,
      description: form.description?.trim() ? form.description.trim() : null,
    };

    try {
      if (editing) {
        // `kind` is create-only: changing it would reprice everything already
        // on the tier from a catalogue it was never offered from.
        const { kind: _kind, ...editable } = payload;
        await updatePlan.mutateAsync({ id: editing.id, input: editable });
      } else {
        await createPlan.mutateAsync(payload);
      }
      close();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not save the plan.");
    }
  }

  async function remove() {
    if (!confirmDelete) return;
    setError(null);
    try {
      await deletePlan.mutateAsync(confirmDelete.id);
      setConfirmDelete(null);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not delete the plan.");
      setConfirmDelete(null);
    }
  }

  const rows = plans.data?.data ?? [];
  const open = creating || editing !== null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Plans"
        description="The resource tiers customers choose from when creating an app. Priced things with no CPU or memory to sell are add-ons instead."
        action={
          <div className="flex gap-2">
            {/* The other half of the price catalogue. Not in the rail: Commerce
                is at the four items that group allows, and add-ons are read
                from here far more often than they are edited. */}
            <Link to="/addons" className={buttonStyles("secondary")}>
              Add-ons
            </Link>
            <Button icon="plus" onClick={openCreate}>
              New plan
            </Button>
          </div>
        }
      />

      {error && !open && <Alert>{error}</Alert>}

      {plans.isLoading ? (
        <Card>
          <TableSkeleton columns={9} />
        </Card>
      ) : rows.length === 0 ? (
        <EmptyState
          icon="plans"
          title="No plans yet"
          description="Customers cannot create apps without an active plan."
          action={<Button onClick={openCreate}>Create the first plan</Button>}
        />
      ) : (
        <Card>
          <Table
            head={
              <tr>
                <Th>Plan</Th>
                <Th priority="sm">For</Th>
                <Th priority="md">CPU</Th>
                <Th priority="md">Memory</Th>
                <Th priority="lg">Storage</Th>
                <Th numeric>Price</Th>
                <Th numeric priority="lg">Apps</Th>
                <Th priority="sm">State</Th>
                <Th />
              </tr>
            }
          >
            {rows.map((plan) => (
              <tr key={plan.id} className={cx(plan.state === "archived" && "opacity-60")}>
                <Td>
                  <div className="font-medium text-fg">{plan.name}</div>
                  <Mono className="text-xs text-fg-subtle">{plan.slug}</Mono>
                </Td>
                <Td priority="sm">
                  <Tag tone={plan.kind === "database" ? "accent" : "neutral"}>
                    {plan.kind === "database" ? "database" : "app"}
                  </Tag>
                </Td>
                <Td priority="md">{plan.resources.cpu} vCPU</Td>
                <Td priority="md">{formatMemory(plan.resources.memoryMb)}</Td>
                {/* Dimmed, unlike CPU and memory beside it: those are container
                    limits the engine applies, this is a figure on a card. */}
                <Td className={cx(plan.storageGb === null && "text-fg-subtle")} priority="lg">
                  {plan.storageGb === null ? "—" : `${plan.storageGb} GB`}
                </Td>
                <Td numeric>{formatPrice(plan.price.cents, plan.price.currency)}/mo</Td>
                <Td numeric priority="lg">{plan.appCount ?? 0}</Td>
                <Td priority="sm">
                  <Tag tone={plan.state === "active" ? "good" : "neutral"}>{plan.state}</Tag>
                </Td>
                <Td className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(plan)}>
                      Edit
                    </Button>
                    {plan.state === "active" ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          void updatePlan.mutateAsync({ id: plan.id, input: { state: "archived" } })
                        }
                      >
                        Archive
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          void updatePlan.mutateAsync({ id: plan.id, input: { state: "active" } })
                        }
                      >
                        Restore
                      </Button>
                    )}
                    {/* Deleting is refused server-side while apps reference the
                        plan; hiding the button when we already know that saves
                        an error the operator cannot act on. */}
                    {(plan.appCount ?? 0) === 0 && (
                      <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(plan)}>
                        Delete
                      </Button>
                    )}
                  </div>
                </Td>
              </tr>
            ))}
          </Table>
          {plans.data && (
            <Pagination
              total={plans.data.total}
              limit={plans.data.limit}
              offset={plans.data.offset}
              onChange={setOffset}
            />
          )}
          <p className="border-t border-line px-4 py-2.5 text-xs text-fg-subtle">
            Archiving keeps existing apps running while removing the plan from the picker.
          </p>
        </Card>
      )}

      <Modal
        open={open}
        onClose={close}
        title={editing ? `Edit ${editing.name}` : "New plan"}
        description={
          editing && (editing.appCount ?? 0) > 0
            ? `${editing.appCount} app(s) run on this plan. Resource changes apply as each redeploys.`
            : "CPU and memory become container limits on the deployment engine."
        }
      >
        <form onSubmit={submit} className="space-y-4">
          {error && <Alert>{error}</Alert>}

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Name" htmlFor="plan-name">
              <Input
                id="plan-name"
                required
                value={form.name}
                onChange={(e) => onNameChange(e.target.value)}
              />
            </Field>
            <Field
              label="Slug"
              htmlFor="plan-slug"
              hint={
                editing
                  ? "Lowercase letters, numbers and hyphens."
                  : slugTouched
                    ? "Lowercase letters, numbers and hyphens."
                    : "Following the name — edit to set your own."
              }
            >
              <Input
                id="plan-slug"
                required
                pattern={SLUG_PATTERN}
                value={form.slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setForm({ ...form, slug: e.target.value });
                }}
              />
            </Field>
          </div>

          <Field
            label="Catalogue"
            htmlFor="plan-kind"
            hint={
              editing
                ? "Cannot be changed — create a tier in the other catalogue instead."
                : "Which picker this tier appears in."
            }
          >
            <Select
              id="plan-kind"
              value={form.kind ?? "app"}
              disabled={editing !== null}
              onChange={(e) => setForm({ ...form, kind: e.target.value as PlanKind })}
            >
              <option value="app">Applications</option>
              <option value="database">Databases</option>
            </Select>
          </Field>

          <Field label="Description" htmlFor="plan-description" hint="Shown on the plan card.">
            <Input
              id="plan-description"
              value={form.description ?? ""}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </Field>

          <fieldset className="space-y-4 border border-line p-4">
            <legend className="px-1 text-xs font-semibold text-fg-subtle">
              Enforced on the deployment engine
            </legend>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="vCPU" htmlFor="plan-cpu" hint="Fractions allowed.">
                <Input
                  id="plan-cpu"
                  type="number"
                  step="0.1"
                  min={0.1}
                  required
                  value={form.cpuLimit}
                  onChange={(e) => setForm({ ...form, cpuLimit: Number(e.target.value) })}
                />
              </Field>
              <Field label="Memory (MB)" htmlFor="plan-memory">
                <Input
                  id="plan-memory"
                  type="number"
                  min={64}
                  required
                  value={form.memoryMb}
                  onChange={(e) => setForm({ ...form, memoryMb: Number(e.target.value) })}
                />
              </Field>
              {/*
                No replicas field. A plan is one container's size; the count
                is the app's own since migration 0151, set by the customer or
                their auto-scale rule, and a figure here would be one nothing
                reads -- the API keeps the column at 1.
              */}
            </div>
          </fieldset>

          {/*
            Disk is genuinely in the category price was wrongly filed under
            once: stated on the card, applied by nothing. Dokploy's database
            update accepts cpuLimit and memoryLimit and no volume quota, so a
            tenant on the 15 GB tier writing 200 GB is stopped by the server
            filling up rather than by anything here.

            Kept out of the fieldset above for that reason — everything under
            "Enforced on the deployment engine" is, and one exception would
            make the legend a lie. The operator reads what the number is worth
            at the moment they type it.
          */}
          <fieldset className="space-y-4 border border-line p-4">
            <legend className="px-1 text-xs font-semibold text-fg-subtle">
              Stated to the customer, enforced by nothing
            </legend>
            <Field
              label="Storage (GB)"
              htmlFor="plan-storage"
              hint="Database tiers only. Nothing caps the volume — watch server disk."
            >
              <Input
                id="plan-storage"
                type="number"
                min={1}
                placeholder="No allowance stated"
                value={form.storageGb ?? ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    // Empty clears it. A tier with no figure states nothing,
                    // which is the honest default and what app tiers carry.
                    storageGb: e.target.value === "" ? null : Number(e.target.value),
                  })
                }
              />
            </Field>
          </fieldset>

          {/*
            Price used to sit inside a "Displayed only — not enforced" fieldset,
            hinted "Nothing is charged" — the exact opposite of the banner on
            this page, and wrong about the one field that moves real money. The
            other occupant of that fieldset was bandwidth, which is gone: it was
            never metered, and stating an allowance contradicted the pricing
            page's own promise that transfer is unmetered.
          */}
          <fieldset className="space-y-4 border border-line p-4">
            <legend className="px-1 text-xs font-semibold text-fg-subtle">
              Billed to the customer
            </legend>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label={`Price per month (${form.currency ?? "USD"})`}
                htmlFor="plan-price"
                hint={`Charged ÷ ${HOURS_PER_MONTH} every hour.`}
              >
                <Input
                  id="plan-price"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min={0}
                  required
                  value={priceInput}
                  onChange={(e) => onPriceChange(e.target.value)}
                  onBlur={() => setPriceInput(toMajor(form.priceCents))}
                />
              </Field>
              <Field label="Currency" htmlFor="plan-currency">
                <Select
                  id="plan-currency"
                  value={form.currency ?? "USD"}
                  onChange={(e) => setForm({ ...form, currency: e.target.value })}
                >
                  {CURRENCIES.map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>

            {/* The number the customer is actually charged, derived as they
                type. Floored through the same helper the server uses, so an
                operator is not shown a rate a fraction above the real one.

                The cap is worth stating beside it: at this divisor a tier is
                paid off after 28 days of continuous running, so the monthly
                figure is what a full month costs rather than a ceiling only
                long months approach. */}
            <p className="text-xs text-fg-subtle">
              <span className="text-fg">
                {rate(hourlyMicros(form.priceCents), form.currency ?? "USD")}
              </span>{" "}
              per hour per app, capped at{" "}
              <span className="text-fg">
                {money(form.priceCents * 10_000, form.currency ?? "USD")}
              </span>{" "}
              a month — reached after {HOURS_PER_MONTH} hours.
            </p>
          </fieldset>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="State" htmlFor="plan-state">
              <Select
                id="plan-state"
                value={form.state ?? "active"}
                onChange={(e) => setForm({ ...form, state: e.target.value as PlanState })}
              >
                <option value="active">Active — customers can choose it</option>
                <option value="archived">Archived — hidden from the picker</option>
              </Select>
            </Field>
            <Field label="Sort order" htmlFor="plan-sort" hint="Lower appears first.">
              <Input
                id="plan-sort"
                type="number"
                min={0}
                value={form.sortOrder ?? 0}
                onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })}
              />
            </Field>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button type="submit" loading={createPlan.isPending || updatePlan.isPending}>
              {editing ? "Save changes" : "Create plan"}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        title={`Delete ${confirmDelete?.name ?? ""}?`}
        description="No app uses this plan. Archiving is usually better."
      >
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setConfirmDelete(null)}>
            Cancel
          </Button>
          <Button variant="danger" loading={deletePlan.isPending} onClick={() => void remove()}>
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
}

function formatPrice(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(cents / 100);
  } catch {
    // An operator can type any three letters into the currency field.
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}
